import { useRef } from "react";
import { ProductResultsV1Url, ProductResultsV2Url } from "../constants";

const useProductResults = () => {
  const productsRef = useRef(null);

  const resetProductResults = () => {
    productsRef.current = null;
  };

  const fetchProductResultsV1 = async (apiToken, lng) => {
    if (productsRef.current) return productsRef.current;

    try {
      const response = await fetch(`${ProductResultsV1Url}?lng=${lng}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        method: ["GET"],
      });
      if (response.ok) {
        const data = await response.json();

        if (!data || typeof data !== "object") {
          throw new Error("Invalid API response");
        }

        const recommendations = Object.entries(data).flatMap(
          ([category, items]) =>
            (items ?? []).map((product) => ({
              ...product,
              name: product.title,
              categories:
                category?.trim() === "" ? ["All-Categories"] : [category],
              brand:
                product.brand?.trim() === "" ? "All-Brands" : product.brand,
            })),
        );

        const categories = [
          ...new Set(
            recommendations
              .flatMap((p) => p.categories || [])
              .filter((c) => c && c !== "All-Categories"),
          ),
        ];

        const brands = [
          ...new Set(
            recommendations
              .map((p) => p.brand)
              .filter((b) => b && b !== "All-Brands"),
          ),
        ];

        productsRef.current = {
          categories: categories,
          brands: brands,
          recommendations: recommendations,
        };
        return productsRef.current;
      } else {
        throw new Error("Failed to fetch!");
      }
    } catch (error) {
      productsRef.current = {
        categories: [],
        brands: [],
        recommendations: [],
      };
      return { categories: [], brands: [], recommendations: [] };
    }
  };

  const fetchProductResultsV2 = async (apiToken, lng) => {
    if (productsRef.current) return productsRef.current;

    try {
      const response = await fetch(`${ProductResultsV2Url}?lng=${lng}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        method: ["GET"],
      });
      if (response.ok) {
        const data = await response.json();

        if (
          !data ||
          (Array.isArray(data) && data.length === 0) ||
          (typeof data === "object" && Object.keys(data).length === 0)
        ) {
          throw new Error("Empty or invalid data");
        }

        productsRef.current = data;
        return data;
      } else {
        throw new Error("Failed to fetch!");
      }
    } catch (error) {
      productsRef.current = {
        categories: [],
        brands: [],
        recommendations: [],
      };
      return { categories: [], brands: [], recommendations: [] };
    }
  };

  return {
    productsRef,
    resetProductResults,
    fetchProductResultsV1,
    fetchProductResultsV2,
  };
};

export default useProductResults;
